import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, publicFileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RamalhoLogo } from "@/components/RamalhoLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  ArrowLeft,
  Upload,
  ImageIcon,
  FileText,
  Zap,
  ExternalLink,
  Trash2,
  Loader2,
  LogOut,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { fmtMoney, fmtKwh, monthLabel } from "@/lib/format";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [client, setClient] = useState(null);
  const [bills, setBills] = useState([]);
  const [generation, setGeneration] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBill, setUploadingBill] = useState(false);
  const [savingGen, setSavingGen] = useState(false);
  const [genForm, setGenForm] = useState({ reference_month: "", generation_kwh: "", notes: "" });
  const logoInputRef = useRef(null);
  const billInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: b }, { data: g }] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/bills`),
        api.get(`/clients/${id}/generation`),
      ]);
      setClient(c);
      setBills(b);
      setGeneration(g);
    } catch (e) {
      toast.error("Falha ao carregar cliente");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/clients/${id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logomarca atualizada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar logomarca");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleBillUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBill(true);
    const tid = toast.loading("Enviando e processando a conta...");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/clients/${id}/bills`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.parsed?._parse_error) {
        toast.warning("Conta enviada, mas não foi possível extrair todos os campos", { id: tid });
      } else {
        toast.success("Conta processada com sucesso", { id: tid });
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao processar conta", { id: tid });
    } finally {
      setUploadingBill(false);
      if (billInputRef.current) billInputRef.current.value = "";
    }
  };

  const handleGenSubmit = async (e) => {
    e.preventDefault();
    setSavingGen(true);
    try {
      await api.post(`/clients/${id}/generation`, {
        reference_month: genForm.reference_month,
        generation_kwh: parseFloat(genForm.generation_kwh),
        notes: genForm.notes || null,
      });
      toast.success("Geração registrada");
      setGenForm({ reference_month: "", generation_kwh: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao registrar geração");
    } finally {
      setSavingGen(false);
    }
  };

  const handleDeleteBill = async (billId) => {
    try {
      await api.delete(`/bills/${billId}`);
      toast.success("Conta removida");
      load();
    } catch {
      toast.error("Falha ao remover");
    }
  };

  const handleDeleteGen = async (genId) => {
    try {
      await api.delete(`/generation/${genId}`);
      toast.success("Registro removido");
      load();
    } catch {
      toast.error("Falha ao remover");
    }
  };

  if (loading || !client) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6]">
        <Loader2 className="h-6 w-6 animate-spin text-[#1B4332]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E5E5DF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              data-testid="back-to-clients"
              className="inline-flex items-center gap-1.5 text-sm text-[#4A4A4A] hover:text-[#1B4332]"
            >
              <ArrowLeft className="h-4 w-4" /> Clientes
            </Link>
            <div className="h-4 w-px bg-[#E5E5DF]" />
            <RamalhoLogo showText={false} size={26} />
          </div>
          <Button
            onClick={() => {
              logout();
              navigate("/login");
            }}
            variant="outline"
            className="rounded-full h-9 text-sm"
            data-testid="logout-btn-detail"
          >
            <LogOut className="h-4 w-4 mr-1.5" /> Sair
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {/* Client header */}
        <div className="flex flex-wrap items-start justify-between gap-6 mb-10">
          <div className="flex items-start gap-5">
            <div className="h-20 w-20 rounded-lg border border-[#E5E5DF] bg-white flex items-center justify-center overflow-hidden">
              {client.logo_path ? (
                <img
                  src={publicFileUrl(client.logo_path)}
                  alt={client.name}
                  className="h-full w-full object-contain p-2"
                  data-testid="client-logo-preview"
                />
              ) : (
                <ImageIcon className="h-6 w-6 text-[#B0B0A8]" />
              )}
            </div>
            <div>
              <span className="overline text-[#52796F]">Cliente</span>
              <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A1A] mt-1">
                {client.name}
              </h1>
              <div className="flex flex-wrap gap-4 text-xs text-[#4A4A4A] mt-3">
                {client.installation_number && (
                  <span>
                    <span className="overline">UC</span>{" "}
                    <span className="mono-num ml-1 text-[#1A1A1A]">{client.installation_number}</span>
                  </span>
                )}
                {client.plant_capacity_kwp != null && (
                  <span>
                    <span className="overline">Capacidade</span>{" "}
                    <span className="mono-num ml-1 text-[#1A1A1A]">{client.plant_capacity_kwp} kWp</span>
                  </span>
                )}
                {client.address && <span>{client.address}</span>}
              </div>
            </div>
          </div>
          <a
            href={`/cliente/${client.slug}`}
            target="_blank"
            rel="noreferrer"
            data-testid="open-public-page"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#1B4332] hover:underline"
          >
            Abrir página pública <ExternalLink className="h-4 w-4" />
          </a>
        </div>

        <Tabs defaultValue="bills" className="w-full">
          <TabsList className="bg-white border border-[#E5E5DF] rounded-full p-1 h-11">
            <TabsTrigger
              value="bills"
              data-testid="tab-bills"
              className="rounded-full data-[state=active]:bg-[#1B4332] data-[state=active]:text-white px-5 h-9"
            >
              Contas EDP
            </TabsTrigger>
            <TabsTrigger
              value="generation"
              data-testid="tab-generation"
              className="rounded-full data-[state=active]:bg-[#1B4332] data-[state=active]:text-white px-5 h-9"
            >
              Geração da usina
            </TabsTrigger>
            <TabsTrigger
              value="logo"
              data-testid="tab-logo"
              className="rounded-full data-[state=active]:bg-[#1B4332] data-[state=active]:text-white px-5 h-9"
            >
              Logomarca
            </TabsTrigger>
          </TabsList>

          {/* Bills */}
          <TabsContent value="bills" className="mt-8">
            <div className="rounded-lg border border-[#E5E5DF] bg-white p-6 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <span className="overline text-[#52796F]">Extração automática</span>
                  <h2 className="font-display text-2xl font-semibold text-[#1A1A1A] mt-1">
                    Enviar conta EDP
                  </h2>
                  <p className="text-sm text-[#4A4A4A] mt-1">
                    PDF ou imagem. Os dados de consumo, créditos, saldo, tarifas e impostos são
                    extraídos automaticamente.
                  </p>
                </div>
                <div>
                  <input
                    ref={billInputRef}
                    type="file"
                    accept=".pdf,image/*"
                    onChange={handleBillUpload}
                    hidden
                    data-testid="bill-file-input"
                  />
                  <Button
                    onClick={() => billInputRef.current?.click()}
                    disabled={uploadingBill}
                    data-testid="upload-bill-btn"
                    className="rounded-full bg-[#1B4332] text-white hover:bg-[#122d22] h-11 px-5"
                  >
                    {uploadingBill ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-1.5" />
                        Enviar conta
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {bills.length === 0 && (
                <div className="rounded-lg border border-dashed border-[#E5E5DF] bg-white p-12 text-center">
                  <FileText className="h-6 w-6 mx-auto text-[#B0B0A8]" />
                  <p className="text-sm text-[#4A4A4A] mt-3">Nenhuma conta enviada ainda</p>
                </div>
              )}
              {bills.map((b) => {
                const p = b.parsed || {};
                return (
                  <div
                    key={b.id}
                    data-testid={`bill-item-${b.id}`}
                    className="rounded-lg border border-[#E5E5DF] bg-white p-6"
                  >
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <span className="overline text-[#52796F]">Referência</span>
                        <h3 className="font-display text-xl font-semibold text-[#1A1A1A] mt-1">
                          {p.referencia_mes || monthLabel(b.created_at?.slice(0, 7)) || b.original_filename}
                        </h3>
                        <p className="text-xs text-[#4A4A4A] mt-1">{b.original_filename}</p>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            className="p-1.5 rounded-md text-[#D90429] hover:bg-[#D90429]/10"
                            data-testid={`delete-bill-${b.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir conta?</AlertDialogTitle>
                            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteBill(b.id)}
                              className="bg-[#D90429] hover:bg-[#a10321]"
                            >
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>

                    {p._parse_error ? (
                      <div className="rounded-md bg-[#FDF3EF] border border-[#E07A5F]/30 text-[#8a3a20] text-xs p-3">
                        Não foi possível extrair os dados desta conta automaticamente. Você pode
                        tentar enviar outra versão do arquivo.
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <MiniStat label="Consumo" value={fmtKwh(p.consumo_kwh)} />
                        <MiniStat label="Créditos utilizados" value={fmtKwh(p.credito_utilizado_kwh)} />
                        <MiniStat label="Saldo créditos" value={fmtKwh(p.saldo_creditos_kwh)} />
                        <MiniStat label="Total" value={fmtMoney(p.valores?.valor_total)} />
                        <MiniStat label="Tarifa TUSD" value={p.tarifas?.tusd_kwh != null ? `R$ ${Number(p.tarifas.tusd_kwh).toFixed(5)}` : "—"} />
                        <MiniStat label="Tarifa TE" value={p.tarifas?.te_kwh != null ? `R$ ${Number(p.tarifas.te_kwh).toFixed(5)}` : "—"} />
                        <MiniStat label="ICMS" value={fmtMoney(p.impostos?.icms_valor)} />
                        <MiniStat label="PIS+COFINS" value={fmtMoney((p.impostos?.pis_valor || 0) + (p.impostos?.cofins_valor || 0))} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Generation */}
          <TabsContent value="generation" className="mt-8">
            <div className="rounded-lg border border-[#E5E5DF] bg-white p-6 mb-6">
              <span className="overline text-[#52796F]">Registrar geração</span>
              <h2 className="font-display text-2xl font-semibold text-[#1A1A1A] mt-1 mb-5">
                Quanto sua usina gerou neste mês?
              </h2>
              <form onSubmit={handleGenSubmit} className="grid md:grid-cols-4 gap-4" data-testid="generation-form">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Mês</Label>
                  <Input
                    type="month"
                    required
                    value={genForm.reference_month}
                    onChange={(e) => setGenForm({ ...genForm, reference_month: e.target.value })}
                    data-testid="gen-month-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
                    Geração (kWh)
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    required
                    value={genForm.generation_kwh}
                    onChange={(e) => setGenForm({ ...genForm, generation_kwh: e.target.value })}
                    placeholder="1250.5"
                    data-testid="gen-kwh-input"
                  />
                </div>
                <div className="md:col-span-2 space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
                    Observação
                  </Label>
                  <Input
                    value={genForm.notes}
                    onChange={(e) => setGenForm({ ...genForm, notes: e.target.value })}
                    placeholder="(opcional)"
                    data-testid="gen-notes-input"
                  />
                </div>
                <div className="md:col-span-4">
                  <Button
                    type="submit"
                    disabled={savingGen}
                    data-testid="gen-submit-btn"
                    className="rounded-full bg-[#1B4332] text-white hover:bg-[#122d22]"
                  >
                    {savingGen ? "Salvando..." : "Registrar geração"}
                  </Button>
                </div>
              </form>
            </div>

            <div className="rounded-lg border border-[#E5E5DF] bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F9F6] border-b border-[#E5E5DF]">
                  <tr className="text-left">
                    <th className="p-4 overline text-[#52796F]">Mês</th>
                    <th className="p-4 overline text-[#52796F]">Geração</th>
                    <th className="p-4 overline text-[#52796F]">Obs.</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {generation.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-8 text-center text-sm text-[#4A4A4A]">
                        Nenhuma geração registrada
                      </td>
                    </tr>
                  )}
                  {generation.map((g) => (
                    <tr key={g.id} className="border-t border-[#E5E5DF]">
                      <td className="p-4">{monthLabel(g.reference_month)}</td>
                      <td className="p-4 mono-num">{fmtKwh(g.generation_kwh)}</td>
                      <td className="p-4 text-[#4A4A4A]">{g.notes || "—"}</td>
                      <td className="p-4 text-right">
                        <button
                          className="p-1.5 rounded-md text-[#D90429] hover:bg-[#D90429]/10"
                          onClick={() => handleDeleteGen(g.id)}
                          data-testid={`delete-gen-${g.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Logo */}
          <TabsContent value="logo" className="mt-8">
            <div className="rounded-lg border border-[#E5E5DF] bg-white p-6">
              <span className="overline text-[#52796F]">Identidade visual</span>
              <h2 className="font-display text-2xl font-semibold text-[#1A1A1A] mt-1">
                Logomarca do cliente
              </h2>
              <p className="text-sm text-[#4A4A4A] mt-1">
                Esta é a marca que aparecerá na página pública do cliente. PNG, JPG, WEBP ou SVG.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-6">
                <div className="h-40 w-40 rounded-lg border border-[#E5E5DF] bg-[#F9F9F6] flex items-center justify-center overflow-hidden">
                  {client.logo_path ? (
                    <img
                      src={publicFileUrl(client.logo_path)}
                      alt={client.name}
                      className="h-full w-full object-contain p-4"
                    />
                  ) : (
                    <div className="text-center text-xs text-[#B0B0A8] px-4">
                      <ImageIcon className="h-6 w-6 mx-auto mb-1" />
                      Nenhuma logomarca
                    </div>
                  )}
                </div>
                <div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*,.svg"
                    onChange={handleLogoUpload}
                    hidden
                    data-testid="logo-file-input"
                  />
                  <Button
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    data-testid="upload-logo-btn"
                    className="rounded-full bg-[#1B4332] text-white hover:bg-[#122d22]"
                  >
                    {uploadingLogo ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-1.5" />
                        {client.logo_path ? "Substituir logomarca" : "Enviar logomarca"}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-md border border-[#E5E5DF] bg-[#F9F9F6] p-3">
      <div className="overline text-[#52796F]">{label}</div>
      <div className="mono-num text-sm font-semibold text-[#1A1A1A] mt-1">{value}</div>
    </div>
  );
}
